import type { UnmatchedRide } from '../../../../types/simulation';

function normalizeTripIdValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

/**
 * Resolves a stable trip identifier from a simulation activity row.
 * Falls back through nested `trips[].ride` fields when top-level ids are absent.
 */
export function resolveActivityTripId(activity: Record<string, unknown>): string {
  const externalCandidates: unknown[] = [activity.external_reference_id];
  const idCandidates: unknown[] = [
    activity.ride_id,
    activity.request_id,
    activity.rider_ride_id,
    activity.driver_ride_id,
  ];

  const trips = activity.trips;
  if (Array.isArray(trips)) {
    for (const trip of trips) {
      if (!trip || typeof trip !== 'object') continue;
      const tripRecord = trip as Record<string, unknown>;
      externalCandidates.push(tripRecord.external_reference_id);
      idCandidates.push(
        tripRecord.ride_id,
        tripRecord.request_id,
        tripRecord.rider_ride_id,
        tripRecord.driver_ride_id
      );

      const ride = tripRecord.ride;
      if (!ride || typeof ride !== 'object') continue;
      const rideRecord = ride as Record<string, unknown>;
      externalCandidates.push(rideRecord.external_reference_id);
      idCandidates.push(
        rideRecord.ride_id,
        rideRecord.request_id,
        rideRecord.rider_ride_id,
        rideRecord.driver_ride_id
      );
    }
  }

  for (const candidate of externalCandidates) {
    const normalized = normalizeTripIdValue(candidate);
    if (normalized) return normalized;
  }

  for (const candidate of idCandidates) {
    const normalized = normalizeTripIdValue(candidate);
    if (normalized) return normalized;
  }

  return '';
}

/**
 * Stable string id for an unmatched ride (map selection + Operations timeline ↔ map).
 */
export function unmatchedRideStableKey(ride: UnmatchedRide): string {
  const id =
    normalizeTripIdValue(ride.ride_id) ??
    normalizeTripIdValue(ride.request_id) ??
    normalizeTripIdValue(ride.external_reference_id) ??
    null;
  if (id) return `um:${id}`;
  const t = typeof ride.request_time === 'string' ? ride.request_time : '';
  return `um:${ride.from_latitude ?? '?'}-${ride.from_longitude ?? '?'}-${ride.to_latitude ?? '?'}-${ride.to_longitude ?? '?'}-${t}`;
}

/**
 * Bins ride requests by hour using API time (UTC). No browser timezone conversion.
 */
export function binRequestsPerHour(ridePoints: any[]): { hour: string; count: number }[] {
  if (!ridePoints) return [];
  const bins: Record<string, number> = {};

  ridePoints.forEach((p) => {
    const timeStr = TIME_STR_KEYS.map((k) => (p as any)[k]).find(Boolean);
    const ms = parseRequestTimeToMs(timeStr);
    if (ms == null) return;
    const d = new Date(ms);
    const y = d.getUTCFullYear();
    const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = d.getUTCDate().toString().padStart(2, '0');
    const h = d.getUTCHours().toString().padStart(2, '0');
    const hourKey = `${y}-${m}-${day} ${h}:00`;
    bins[hourKey] = (bins[hourKey] || 0) + 1;
  });

  return Object.entries(bins)
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour.localeCompare(b.hour));
}

const TIME_STR_REGEX = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/;

/** True if the string has an explicit timezone (Z or ±HH:MM). */
function hasTimezoneOffset(s: string): boolean {
  return /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s.trim());
}

/**
 * Parse request_time (or similar) to milliseconds. Uses API time only (no browser timezone).
 * Strings with "Z" or "+00:00" etc. are parsed as-is. Naive strings (no offset) are treated as UTC
 * so the literal time from the API is preserved. Timeline and charts display in UTC.
 */
export function parseRequestTimeToMs(timeStr: string | undefined): number | null {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const normalized = hasTimezoneOffset(timeStr) ? timeStr : timeStr.trim() + 'Z';
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : ms;
}

const TIME_STR_KEYS = ['request_time', 'pick_up_start_time', 'pick_up_end_time'];

/**
 * Filter ride points to those within the time window [currentTimeMs - windowMinutes*60*1000, currentTimeMs].
 * Used to show on the map only trips in the selected "corridor" (e.g. last 1 hour).
 */
export function filterRidePointsByTimeWindow(
  ridePoints: any[],
  currentTimeMs: number,
  windowMinutes: number
): any[] {
  if (!ridePoints || ridePoints.length === 0) return [];
  const windowMs = windowMinutes * 60 * 1000;
  const startMs = currentTimeMs - windowMs;
  return ridePoints.filter((p) => {
    const timeStr = TIME_STR_KEYS.map((k) => (p as any)[k]).find(Boolean);
    const ts = parseRequestTimeToMs(timeStr);
    return ts != null && ts >= startMs && ts <= currentTimeMs;
  });
}

/**
 * Request/pickup times (ms, UTC) for the trips carried by a road route, resolved through the
 * ride-point id map. Uses the SAME time basis as the timeline histogram (`TIME_STR_KEYS`) so a
 * road path filters by the timeline window exactly like the trip(s) it represents — for every
 * route, regardless of whether segment drive-interval metadata is present.
 *
 * Candidate ids are the route's onboard-ride ids plus the `routeId` itself, since flat
 * `/results/{id}/paths` payloads key each row by `ride_id`/`driver_ride_id` while segment-style
 * payloads carry `rides_onboard_ids`. Returns an empty array for trip-less legs (e.g. deadhead).
 */
export function roadRouteRequestTimesMs(
  route: { routeId: string; metadata?: { rides_onboard_ids?: string[] } },
  ridePointByIdMap: Map<string, Record<string, unknown>>
): number[] {
  const ids = new Set<string>();
  (route.metadata?.rides_onboard_ids ?? []).forEach((id) => {
    if (typeof id === 'string' && id.trim() !== '') ids.add(id);
  });
  if (route.routeId) ids.add(route.routeId);

  const times: number[] = [];
  ids.forEach((id) => {
    const point = ridePointByIdMap.get(id);
    if (!point) return;
    const timeStr = TIME_STR_KEYS.map((k) => point[k]).find(Boolean) as string | undefined;
    const ms = parseRequestTimeToMs(timeStr);
    if (ms != null) times.push(ms);
  });
  return times;
}

function get15MinBucketMs(timeStr: string | undefined): number | null {
  const ms = parseRequestTimeToMs(timeStr);
  if (ms == null) return null;
  return Math.floor(ms / (15 * 60 * 1000)) * (15 * 60 * 1000);
}

/**
 * Bins ride requests by 15-minute intervals. Returns bins with timestamp (ms) and count.
 * Used for the Timeline histogram.
 */
export function binRequestsPer15Min(ridePoints: any[]): { timestampMs: number; label: string; count: number }[] {
  if (!ridePoints || ridePoints.length === 0) return [];
  const bins: Record<number, number> = {};

  ridePoints.forEach((p) => {
    const timeStr = TIME_STR_KEYS.map((k) => (p as any)[k]).find(Boolean);
    const quarter = get15MinBucketMs(timeStr);
    if (quarter == null) return;
    bins[quarter] = (bins[quarter] || 0) + 1;
  });

  return Object.entries(bins)
    .map(([quarterMs, count]) => {
      const t = parseInt(quarterMs, 10);
      const d = new Date(t);
      const label = `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
      return { timestampMs: t, label, count };
    })
    .sort((a, b) => a.timestampMs - b.timestampMs);
}

/**
 * Returns min and max timestamp (ms) from ride_points request_time fields.
 */
export function getRidePointsTimeRange(ridePoints: any[]): { minMs: number; maxMs: number } | null {
  if (!ridePoints || ridePoints.length === 0) return null;
  let minMs = Infinity;
  let maxMs = -Infinity;
  ridePoints.forEach((p) => {
    const timeStr = TIME_STR_KEYS.map((k) => (p as any)[k]).find(Boolean);
    const ts = parseRequestTimeToMs(timeStr);
    if (ts != null) {
      minMs = Math.min(minMs, ts);
      maxMs = Math.max(maxMs, ts);
    }
  });
  if (minMs === Infinity || maxMs === -Infinity) return null;
  return { minMs, maxMs };
}

/**
 * Normalizes lat/lng from simulation APIs (often JSON numbers, sometimes numeric strings).
 */
export function coerceRideCoordinate(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Builds a Google Maps driving-directions URL (pickup → dropoff).
 * Prefers coordinates; falls back to from/to addresses when coords are missing.
 * When `request_time` parses, appends `departure_time` (Unix seconds) as a hint; web Maps
 * may still require the user to set Depart at / Leave at for time-of-day traffic.
 */
export function buildUnmatchedRideGoogleMapsDrivingUrl(ride: UnmatchedRide): string | null {
  const a = coerceRideCoordinate(ride.from_latitude);
  const b = coerceRideCoordinate(ride.from_longitude);
  const c = coerceRideCoordinate(ride.to_latitude);
  const d = coerceRideCoordinate(ride.to_longitude);
  const fa = typeof ride.from_address === 'string' ? ride.from_address.trim() : '';
  const ta = typeof ride.to_address === 'string' ? ride.to_address.trim() : '';

  let origin: string | null = null;
  let dest: string | null = null;

  if (a != null && b != null && c != null && d != null) {
    origin = `${a},${b}`;
    dest = `${c},${d}`;
  } else if (fa !== '' && ta !== '') {
    origin = fa;
    dest = ta;
  } else {
    return null;
  }

  let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
  const depMs = parseRequestTimeToMs(
    typeof ride.request_time === 'string' ? ride.request_time : undefined
  );
  // Hint requested time-of-day; consumer Maps may ignore — tooltip still tells users to set “Depart at”.
  if (depMs != null) {
    url += `&departure_time=${Math.floor(depMs / 1000)}`;
  }
  return url;
}
